import { Button } from "components/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/ui/tooltip";
import { cn } from "utils";
import type {
  SalesDatePresetId,
  SalesDateRange,
} from "shared/salesData/salesDateRange";

export interface RangePreset {
  id: SalesDatePresetId;
  getRange: () => SalesDateRange | undefined;
  getUnclampedStart: () => Date;
}

interface SalesDateRangePickerPresetsProps {
  presets: RangePreset[];
  presetLabels: Record<SalesDatePresetId, string>;
  activePresetId: SalesDatePresetId | undefined;
  lastYearDisabledReason: string | undefined;
  onPresetSelect: (preset: RangePreset) => void;
}

export const SalesDateRangePickerPresets = ({
  presets,
  presetLabels,
  activePresetId,
  lastYearDisabledReason,
  onPresetSelect,
}: SalesDateRangePickerPresetsProps) => (
  <div className="flex flex-wrap gap-1.5">
    {presets.map((preset) => {
      const presetRange = preset.getRange();
      const isActive = preset.id === activePresetId;
      const isDisabled = !presetRange;
      const disabledReason =
        preset.id === "last-year" ? lastYearDisabledReason : undefined;

      return (
        <Tooltip key={preset.id} delayDuration={200}>
          <TooltipTrigger asChild>
            <span
              className={cn(
                "inline-flex rounded-md",
                isDisabled && disabledReason && "cursor-not-allowed",
              )}
              role={isDisabled && disabledReason ? "button" : undefined}
              tabIndex={isDisabled && disabledReason ? 0 : undefined}
              data-testid={
                isDisabled && disabledReason
                  ? `sales-date-range-preset-${preset.id}-trigger`
                  : undefined
              }
            >
              <Button
                variant={isActive ? "secondary" : "tertiary"}
                size="small"
                className={cn(
                  isActive ? "bg-primary-50" : undefined,
                  isDisabled && disabledReason && "pointer-events-none",
                )}
                label={presetLabels[preset.id]}
                disabled={isDisabled}
                onClick={() => {
                  if (!presetRange) {
                    return;
                  }
                  onPresetSelect(preset);
                }}
                testId={`sales-date-range-preset-${preset.id}`}
              />
            </span>
          </TooltipTrigger>
          {disabledReason ? (
            <TooltipContent
              side="top"
              className="z-[100] max-w-xs text-left text-sm shadow-tooltip"
            >
              {disabledReason}
            </TooltipContent>
          ) : null}
        </Tooltip>
      );
    })}
  </div>
);
